import type { PrvT } from "./usePrvTranslation";
import moment from "moment";
import { type PrvPattern, type PrvPatternDay, PrvSchedule } from "#/types/prv";

export type PrvBucket = "week" | "month";

const DAY_MS = 86_400_000;

/** Days between the first arrival and the latest activity, at least 1. */
export function observedSpanDays(pattern: PrvPattern): number {
  if (!pattern.firstMovementAt || !pattern.lastMovementAt) return 1;
  const span =
    (new Date(pattern.lastMovementAt).getTime() - new Date(pattern.firstMovementAt).getTime()) /
    DAY_MS;
  return Math.max(1, Math.ceil(span));
}

/** Stays per `periodDays`, from the observed span. Null when nothing observed. */
export function itemsPerPeriod(pattern: PrvPattern, periodDays: number): number | null {
  if (pattern.stays <= 0) return null;
  return (pattern.stays / observedSpanDays(pattern)) * periodDays;
}

/** Period used for the rhythm of a pattern: the client's target days when defined, else 30. */
export function rhythmPeriodDays(pattern: PrvPattern): number {
  return pattern.target?.days && pattern.target.days > 0 ? pattern.target.days : 30;
}

/** Comparable intensity across the heatmap: stays per 30 days. */
export function intensityPer30Days(pattern: PrvPattern): number {
  return itemsPerPeriod(pattern, 30) ?? 0;
}

/** Average duration of the completed stays (open ones have no duration yet). */
export function averageStaySeconds(pattern: PrvPattern): number | null {
  if (pattern.completedStays <= 0) return null;
  return pattern.totalStaySeconds / pattern.completedStays;
}

/** "3d 4h", "5h 20m", "45m". */
export function formatDuration(seconds: number | null | undefined, t: PrvT): string {
  if (seconds === null || seconds === undefined || !Number.isFinite(seconds)) return "--";
  const total = Math.max(0, Math.round(seconds));
  const days = Math.floor(total / 86_400);
  const hours = Math.floor((total % 86_400) / 3_600);
  const minutes = Math.floor((total % 3_600) / 60);
  if (days > 0) return t("clients.prv.duration.daysHours", { days, hours });
  if (hours > 0) return t("clients.prv.duration.hoursMinutes", { hours, minutes });
  return t("clients.prv.duration.minutes", { minutes });
}

export function runDurationSeconds(run: {
  startedAt: string;
  finishedAt: string | null;
}): number | null {
  if (!run.finishedAt) return null;
  return (new Date(run.finishedAt).getTime() - new Date(run.startedAt).getTime()) / 1000;
}

/** Start of the UTC week (Monday) or month containing `day`, as the bucket key. */
export function bucketKey(day: string, bucket: PrvBucket): string {
  const date = moment.utc(day, "YYYY-MM-DD");
  if (!date.isValid()) return day;
  return bucket === "week"
    ? date.isoWeekday(1).format("YYYY-MM-DD")
    : date.startOf("month").format("YYYY-MM-DD");
}

/** Axis label of a bucket: its first day, in the client's own date format like every other date. */
export function bucketLabel(key: string, dateFormat: string): string {
  return moment.utc(key, "YYYY-MM-DD").format(dateFormat);
}

export interface TimelinePoint {
  key: string;
  label: string;
  arrivals: number;
  /** Stored negative so bars hang below the axis. */
  departures: number;
  /** Items at the sub-client at the end of the bucket: stays started minus stays ended by then. */
  staying: number;
}

/**
 * Roll daily buckets of the given patterns up into a continuous time series
 * (empty buckets filled with zeros so the axis has no gaps).
 */
export function buildTimeline(
  days: PrvPatternDay[],
  patternIds: Set<number>,
  bucket: PrvBucket,
  dateFormat: string
): TimelinePoint[] {
  const totals = new Map<string, { arrivals: number; departures: number }>();
  for (const day of days) {
    if (!patternIds.has(day.patternId)) continue;
    const key = bucketKey(day.day, bucket);
    const entry = totals.get(key) ?? { arrivals: 0, departures: 0 };
    entry.arrivals += day.arrivals;
    entry.departures += day.departures;
    totals.set(key, entry);
  }
  const keys = [...totals.keys()].sort();
  if (keys.length === 0) return [];

  const points: TimelinePoint[] = [];
  let staying = 0;
  const cursor = moment.utc(keys[0], "YYYY-MM-DD");
  const last = moment.utc(keys[keys.length - 1], "YYYY-MM-DD");
  while (cursor.isSameOrBefore(last)) {
    const key = cursor.format("YYYY-MM-DD");
    const entry = totals.get(key) ?? { arrivals: 0, departures: 0 };
    staying = Math.max(0, staying + entry.arrivals - entry.departures);
    points.push({
      key,
      label: bucketLabel(key, dateFormat),
      arrivals: entry.arrivals,
      departures: -entry.departures,
      staying,
    });
    cursor.add(1, bucket === "week" ? "week" : "month");
  }
  return points;
}

/** Tolerance around the target that still counts as met (user's rule: ±10 %). */
export const TARGET_TOLERANCE = 0.1;

export type TargetStatus = "met" | "below" | "overflow";

/** One rotation target checked against the latest interval. */
export interface TargetCheck {
  /** Target: `count` items every `days` days. */
  count: number;
  days: number;
  /** Arrivals in the last `days` days ending at the last run. */
  arrived: number;
  /** met = within ±10 % of the target; below = under that band; overflow = above it. */
  status: TargetStatus;
}

/** Where `arrived` sits against `count`: within ±10 % is met, less is below, more is overflow. */
export function targetStatus(arrived: number, count: number): TargetStatus {
  const tolerance = count * TARGET_TOLERANCE;
  if (arrived < count - tolerance) return "below";
  if (arrived > count + tolerance) return "overflow";
  return "met";
}

/**
 * Arrivals of a pattern in the latest interval: the `windowDays` UTC days
 * ending on (and including) the day of `asOf`.
 */
export function arrivalsInLatestInterval(
  days: PrvPatternDay[],
  patternId: number,
  windowDays: number,
  asOf: string
): number {
  const end = moment.utc(asOf).startOf("day");
  const start = end.clone().subtract(windowDays - 1, "days");
  let total = 0;
  for (const day of days) {
    if (day.patternId !== patternId) continue;
    const at = moment.utc(day.day, "YYYY-MM-DD");
    if (at.isSameOrAfter(start) && at.isSameOrBefore(end)) total += day.arrivals;
  }
  return total;
}

/** The pattern's target checked against its latest interval; null when it has no target. */
export function checkTarget(
  pattern: PrvPattern,
  days: PrvPatternDay[],
  asOf: string
): TargetCheck | null {
  if (!pattern.target || pattern.target.days <= 0) return null;
  const arrived =
    pattern.id === null ? 0 : arrivalsInLatestInterval(days, pattern.id, pattern.target.days, asOf);
  return {
    count: pattern.target.count,
    days: pattern.target.days,
    arrived,
    status: targetStatus(arrived, pattern.target.count),
  };
}

/** The target expressed per bucket: `count` items every `days` days → items per week/month. */
export function targetPerBucket(
  target: { days: number; count: number } | null,
  bucket: PrvBucket
): number | null {
  if (!target || target.days <= 0) return null;
  const bucketDays = bucket === "week" ? 7 : 30;
  return (target.count / target.days) * bucketDays;
}

/**
 * When the nightly scheduler will next consider this client, mirroring the
 * backend's calendar-day rule (sweep at 02:00 UTC). Null when paused or
 * unknown.
 */
export function nextScheduledRun(schedule: PrvSchedule, lastStartedAt: string | null): Date | null {
  if (schedule === PrvSchedule.PAUSED) return null;
  const sweepHourUtc = 2;
  const now = moment.utc();
  if (!lastStartedAt) {
    const next = now.clone().startOf("day").hour(sweepHourUtc);
    return (next.isAfter(now) ? next : next.add(1, "day")).toDate();
  }
  const last = moment.utc(lastStartedAt).startOf("day");
  const next =
    schedule === PrvSchedule.MONTHLY
      ? last.clone().add(1, "month").startOf("month")
      : last
          .clone()
          .add(
            schedule === PrvSchedule.DAILY ? 1 : schedule === PrvSchedule.WEEKLY ? 7 : 14,
            "days"
          );
  next.hour(sweepHourUtc);
  // If that moment already passed, the next sweep picks it up.
  if (next.isBefore(now)) {
    const upcoming = now.clone().startOf("day").hour(sweepHourUtc);
    return (upcoming.isAfter(now) ? upcoming : upcoming.add(1, "day")).toDate();
  }
  return next.toDate();
}
